'use strict';
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

/**
 * useBandwidthRestriction
 *
 * Checks the platform's CDN bandwidth restriction status on mount and
 * provides a manual re-check trigger (used by the modal's Re-check button).
 *
 * Behaviour contract:
 *  – Calls GET /Front-End/bandwidth-status once on mount (uses 60 s server cache).
 *  – recheck() passes ?force=1 to bypass the cache and get a live Bunny CDN reading.
 *  – Always fails open: on network/API error `restricted` stays false so users
 *    are never blocked by a monitoring failure.
 *  – Cleans up on unmount (ignores stale responses via mountedRef).
 *  – Returns a stable `recheck` reference (useCallback) to prevent needless re-renders.
 *
 * @returns {{ restricted: boolean, warned: boolean, bunnyCdnEnabled: boolean,
 *             bandwidthInfo: object|null, checking: boolean, recheck: function }}
 */
export default function useBandwidthRestriction() {
    const [bandwidthInfo, setBandwidthInfo]     = useState(null);
    const [restricted,    setRestricted]        = useState(false);
    const [warned,        setWarned]            = useState(false);
    const [bunnyCdnEnabled, setBunnyCdnEnabled] = useState(false);
    const [checking,      setChecking]          = useState(false);

    // Guard against state updates after unmount
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const fetchStatus = useCallback(async (force = false) => {
        if (!mountedRef.current) return;
        setChecking(true);

        try {
            const baseURL = process.env.REACT_APP_Baseurl;
            const url     = `${baseURL}/Front-End/bandwidth-status${force ? '?force=1' : ''}`;
            const { data } = await axios.get(url);

            if (!mountedRef.current) return;   // unmounted while request was in flight

            const cdnEnabled  = data?.bunnyCdnEnabled === true;
            const isRestricted = cdnEnabled && data?.restricted === true;
            const isWarned     = cdnEnabled && data?.warned     === true;

            setBunnyCdnEnabled(cdnEnabled);
            setRestricted(isRestricted);
            setWarned(isWarned);
            setBandwidthInfo(isRestricted || isWarned ? {
                monthlyUsedGB:  data?.monthlyUsedGB  ?? 0,
                monthlyLimitGB: data?.monthlyLimitGB ?? 0,
                usagePercent:   data?.usagePercent   ?? 0,
            } : null);
        } catch (_err) {
            // Fail-open: a network/API error must never block the user
            if (mountedRef.current) {
                setRestricted(false);
                setWarned(false);
                setBandwidthInfo(null);
            }
        } finally {
            if (mountedRef.current) setChecking(false);
        }
    }, []); // stable — no external deps

    // Check once on mount (non-blocking, uses server-side cache)
    useEffect(() => {
        fetchStatus(false);
    }, [fetchStatus]);

    // Stable re-check callback passed to the modal's Re-check button
    const recheck = useCallback(() => fetchStatus(true), [fetchStatus]);

    return { restricted, warned, bunnyCdnEnabled, bandwidthInfo, checking, recheck };
}
